import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import '../styles/plans.css';

interface Plan {
  code: string;
  name: string;
  tagline: string;
  order: number;
  limits: {
    companies: number;
    users: number;
    documentsPerMonth: number;
    storageMb: number;
    aiCreditsPerMonth: number;
    ocrPagesPerMonth: number;
  };
}

const fmtLimit = (n: number): string => (n < 0 ? 'Ilimitado' : n.toLocaleString('es-ES'));

const KEY_LIMITS: Array<{ key: keyof Plan['limits']; label: string }> = [
  { key: 'companies', label: 'Empresas' },
  { key: 'users', label: 'Usuarios' },
  { key: 'documentsPerMonth', label: 'Documentos / mes' },
  { key: 'aiCreditsPerMonth', label: 'AI credits / month' },
];

export function Subscription() {
  const { me, refresh } = useSession();
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<Plan[]>('/plans').then((p) => setPlans(p.slice().sort((a, b) => a.order - b.order))).catch(() => void 0);
  }, []);

  const current = me?.workspace.plan;
  const currentOrder = plans?.find((p) => p.code === current)?.order ?? 0;

  const change = async (code: string) => {
    setBusy(code);
    setErr(null);
    try {
      await api.post('/workspace/plan', { plan: code });
      // Reevaluar: cambia lo que se puede agor y, por tanto, la navegación.
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'No se pudo cambiar el plan.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <main className="main">
      <div className="page-head">
        <div className="page-head__title">
          <span className="eyebrow">Administration</span>
          <h1 className="h1">Subscription</h1>
          <p className="page-head__sub">
            Your plan defines the limits and which modules are available. When you change it, the
            objectives and menu recalculate instantly to reflect what you can now do.
          </p>
        </div>
      </div>

      {err && <div className="act-error" style={{ marginBottom: 18 }}><Icon name="alert" size={16} />{err}</div>}

      {!plans ? (
        <div className="faint" style={{ padding: 20 }}>Cargando planes…</div>
      ) : (
        <div className="plans">
          {plans.map((p) => {
            const isCurrent = p.code === current;
            const direction = p.order > currentOrder ? 'up' : 'down';
            return (
              <div key={p.code} className={`plan${isCurrent ? ' plan--current' : ''}`}>
                {isCurrent && <span className="plan__badge">Plan actual</span>}
                <div className="plan__name">{p.name}</div>
                <div className="plan__tag">{p.tagline}</div>

                <div className="plan__limits">
                  {KEY_LIMITS.map(({ key, label }) => (
                    <div className="plan__limit" key={key}>
                      <Icon name="check" size={15} className="plan__limit-ico" />
                      <span className="num">{fmtLimit(p.limits[key])}</span>
                      <span className="muted">{label}</span>
                    </div>
                  ))}
                </div>

                {isCurrent ? (
                  <button className="btn btn--ghost" disabled style={{ opacity: 0.6 }}>
                    En uso
                  </button>
                ) : (
                  <button
                    className={`btn ${direction === 'up' ? 'btn--primary' : 'btn--ghost'}`}
                    disabled={busy !== null}
                    onClick={() => change(p.code)}
                  >
                    {busy === p.code ? 'Aplicando…' : direction === 'up' ? 'Subir a este plan' : 'Cambiar a este plan'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}
